import { AboutView } from './about.jsx';
import { useIsMobile, usePersistentState } from './hooks.js';
import { LandingPage } from './landing.jsx';
import { MS_BEGONNEN, MS_BEGONNEN_TEXT, MS_DURCH, MS_DURCH_TEXT, buildHash, getPatternProgress, loadGlobal, parseHash, saveGlobal } from './logic.js';
import { MeinBereichView } from './mein-bereich.jsx';
import { GroupNav, Header, SearchOverlay } from './navigation.jsx';
import { ErkennenPanel, ReagierenPanel, UnterscheidenPanel } from './panels.jsx';
import { ReflexionModal } from './reflexion.jsx';
import { MilestoneToast } from './shared.jsx';
import { accent, border, textDim } from './tokens.js';

const { useState, useEffect } = React;

// ─── Haupt-App ───────────────────────────────────────────────────────────
function App() {
  const [view, setView]           = usePersistentState("view", "landing");
  const [patternId, setPatternId] = usePersistentState("patternId", null);
  const [mode, setMode]           = usePersistentState("mode", "erkennen");
  const [reflexionOpen, setReflexionOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [userName, setUserName] = usePersistentState("global.userName", "");
  const [milestoneText, setMilestoneText] = useState(null);
  const [meinTab, setMeinTab] = useState("erkundung");
  const mobile = useIsMobile();

  useEffect(() => {
    if (view === "pattern" && (!patternId || !PATTERNS[patternId])) {
      setView("landing");
      setPatternId(null);
    }
  }, [view, patternId]);

  // Erkundungs-Tracking + Meilensteine
  useEffect(() => {
    if (view !== "pattern" || !patternId || !PATTERNS[patternId]) return;
    saveGlobal("lastVisited", patternId);
    const visited = loadGlobal("visited") || [];
    if (!visited.includes(patternId)) {
      const next = [...visited, patternId];
      saveGlobal("visited", next);
      const achieved = loadGlobal("msBegonnen") || [];
      const threshold = MS_BEGONNEN.find(t => next.length >= t && !achieved.includes(t));
      if (threshold) {
        saveGlobal("msBegonnen", [...achieved, threshold]);
        setMilestoneText(MS_BEGONNEN_TEXT[threshold]);
      }
    }
  }, [view, patternId]);

  // ─── Hash-Routing: Browser-Zurueck/Vor + Deep-Links ───
  const skipHashSync = React.useRef(true);
  // Mount: Deep-Link aus Hash uebernehmen; sonst persistierten Stand im Hash spiegeln
  useEffect(() => {
    if (window.location.hash.replace(/^#/, "") !== "") {
      const h = parseHash();
      setView(h.view); setPatternId(h.patternId); setMode(h.mode);
      if (h.meinTab) setMeinTab(h.meinTab);
    } else {
      const t = buildHash(view, patternId, mode, meinTab);
      if (t) window.history.replaceState(null, "", t);
    }
  }, []);
  // State -> Hash (erster Lauf uebersprungen, damit der Mount-Reconcile nicht ueberschrieben wird)
  useEffect(() => {
    if (skipHashSync.current) { skipHashSync.current = false; return; }
    const t = buildHash(view, patternId, mode, meinTab);
    if (t.replace(/^#/, "") !== window.location.hash.replace(/^#/, "")) {
      window.location.hash = t;
    }
  }, [view, patternId, mode, meinTab]);
  // Hash -> State (loest bei Browser-Zurueck/Vor aus)
  useEffect(() => {
    const onHash = () => {
      const h = parseHash();
      setView(v => v !== h.view ? h.view : v);
      setPatternId(p => p !== h.patternId ? h.patternId : p);
      setMode(m => m !== h.mode ? h.mode : m);
      if (h.meinTab) setMeinTab(mt => mt !== h.meinTab ? h.meinTab : mt);
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  // Durch-Meilensteine: feuern, wenn ein Muster durchgearbeitet wird (aus den Panels)
  const checkDurchMilestones = React.useCallback(() => {
    const count = Object.keys(PATTERNS).filter(id => getPatternProgress(id) === "durchgearbeitet").length;
    const achieved = loadGlobal("msDurch") || [];
    const threshold = MS_DURCH.find(t => count >= t && !achieved.includes(t));
    if (threshold) {
      saveGlobal("msDurch", [...achieved, threshold]);
      setMilestoneText(MS_DURCH_TEXT[threshold]);
    }
  }, []);

  // Globaler "/" Shortcut zum Oeffnen der Suche (nicht bei aktivem Texteingabe-Element)
  useEffect(() => {
    const h = (e) => {
      if (e.key !== "/") return;
      const t = e.target;
      if (!t) return;
      const tag = (t.tagName || "").toUpperCase();
      if (tag === "INPUT" || tag === "TEXTAREA" || t.isContentEditable) return;
      e.preventDefault();
      setSearchOpen(true);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  // ScrollTop bei View-Wechsel oder Pattern-Wechsel
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [view, patternId]);

  // Zentrale ESC-Verwaltung: schliesst nur das oberste Overlay (Search > Reflexion)
  useEffect(() => {
    if (!searchOpen && !reflexionOpen) return;
    const h = (e) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      if (searchOpen) { setSearchOpen(false); return; }
      if (reflexionOpen) { setReflexionOpen(false); return; }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [searchOpen, reflexionOpen]);

  function pickSearchResult(id) {
    setPatternId(id);
    setMode("erkennen");
    setView("pattern");
    setSearchOpen(false);
  }

  if (view === "landing") {
    return (
      <>
        <LandingPage
          onSelectGroup={(key) => { setPatternId(GROUPS[key].members[0]); setMode("erkennen"); setView("pattern"); }}
          onAbout={() => setView("about")}
          onSelectPattern={(id) => { setPatternId(id); setMode("erkennen"); setView("pattern"); }}
          onMein={() => setView("mein")}
        />
        {searchOpen && (
          <SearchOverlay onClose={() => setSearchOpen(false)} onPick={pickSearchResult} />
        )}
      </>
    );
  }

  const goLanding = () => { setView("landing"); setPatternId(null); };
  const goAbout   = () => setView("about");
  const goMein    = (tab) => { if (typeof tab === "string") setMeinTab(tab); setView("mein"); };
  const onSelectWithMode = (id, m) => { setPatternId(id); setMode(m); setView("pattern"); };

  if (view === "about") {
    return (
      <>
        <AboutView
          onBack={() => setView(patternId ? "pattern" : "landing")}
          onSelectPattern={(id) => { setPatternId(id); setMode("erkennen"); setView("pattern"); }}
          onSelectWithMode={onSelectWithMode}
          onMein={goMein}
        />
        {searchOpen && (
          <SearchOverlay onClose={() => setSearchOpen(false)} onPick={pickSearchResult} />
        )}
      </>
    );
  }

  if (view === "mein") {
    return (
      <MeinBereichView
        onLogo={goLanding}
        onAbout={goAbout}
        onMein={goMein}
        tab={meinTab}
        onTab={setMeinTab}
        onSelect={(id) => { setPatternId(id); setMode("erkennen"); setView("pattern"); }}
        onSelectWithMode={onSelectWithMode}
        userName={userName}
        onNameChange={setUserName}
      />
    );
  }

  const p = PATTERNS[patternId];
  if (!p) {
    return <div style={{padding: 24, color: textDim}}>Lade Muster ...</div>;
  }
  return (
    <>
      <Header onLogo={goLanding} onAbout={goAbout} onReflexion={() => setReflexionOpen(true)} onSearch={() => setSearchOpen(true)} onMein={goMein} userName={userName} />
      <GroupNav
        currentPatternId={patternId}
        onSelect={(id) => { if (id !== patternId) setMode("erkennen"); setPatternId(id); }}
      />

      {/* Modi-Tableiste */}
      <nav style={{
        display: "flex", justifyContent: "center", gap: mobile ? 8 : 24,
        padding: "0 18px", borderBottom: `1px solid ${border}`
      }}>
        {[["erkennen", "01 Erkennen"], ["unterscheiden", "02 Unterscheiden"], ["reagieren", "03 Reagieren"]].map(([m, label]) => {
          const active = mode === m;
          return (
            <button key={m} onClick={() => setMode(m)}
                    style={{
                      background: "none", border: "none", padding: mobile ? "9px 12px" : "10px 22px",
                      color: active ? accent : textDim,
                      borderBottom: `2px solid ${active ? accent : "transparent"}`,
                      cursor: "pointer", fontFamily: "inherit",
                      fontSize: 12, letterSpacing: "0.03em"
                    }}>
              {label}
            </button>
          );
        })}
      </nav>

      <main>
        {mode === "erkennen" && <ErkennenPanel key={p.id} pattern={p} />}
        {mode === "unterscheiden" && (
          <UnterscheidenPanel key={p.id} pattern={p} onJumpTo={(id) => { setPatternId(id); setMode("erkennen"); }} onProgress={checkDurchMilestones} />
        )}
        {mode === "reagieren" && <ReagierenPanel key={p.id} pattern={p} onSelectWithMode={onSelectWithMode} onProgress={checkDurchMilestones} />}
      </main>

      {reflexionOpen && (
        <ReflexionModal patternId={patternId} onClose={() => setReflexionOpen(false)} />
      )}
      {searchOpen && (
        <SearchOverlay onClose={() => setSearchOpen(false)} onPick={pickSearchResult} />
      )}
      {milestoneText && (
        <MilestoneToast text={milestoneText} onClose={() => setMilestoneText(null)} />
      )}
    </>
  );
}

ReactDOM.createRoot(document.getElementById("root")).render(<App />);
